import { useState } from 'react';

import { Flex, Modal, ModalBody, ModalContent, ModalFooter, ModalOverlay, Spinner, useTheme } from '@chakra-ui/react';
import { Button } from '@mesh.ai/rabbit';
import { useQueryClient } from '@tanstack/react-query';

import { isMobileView } from 'constants/isMobileView';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { uploadPdf, useAcknowledgeNotePoll } from '../dataHooks';
import { VIOLATION_STATUS } from '../types';

type ViewViolationEmployeeViewFooterProps = {
  violationNoteId: number;
  violationStatus: keyof typeof VIOLATION_STATUS;
  onCancel: () => void;
  generatePdfFormData: () => Promise<FormData>;
};

const SigningModal = ({
  isOpen,
  onClose,
  signingLink,
  isLoading,
  isNoteStatusUpdating,
}: {
  isOpen: boolean;
  onClose: () => void;
  signingLink: string;
  isLoading: boolean;
  isNoteStatusUpdating: boolean;
}) => {
  const theme = useTheme();

  return (
    <Modal isOpen={isOpen} isCentered size="5xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        w="900px"
        h="calc(100vh - 98px)"
        position="relative"
        gap={0}
        margin={isMobileView() ? '10px' : '0px'}
      >
        <ModalBody p="24px" color={theme.colors.text.g1}>
          {isLoading ? (
            <TextBodyMedium>Loading your report...</TextBodyMedium>
          ) : (
            <iframe
              title="e-sign-report"
              id="e-sign-report"
              style={{
                width: '100%',
                height: 'calc(100vh - 198px)',
                borderRadius: '8px',
                overflow: 'auto',
                boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.24)',
              }}
              src={signingLink}
            />
          )}
        </ModalBody>
        <ModalFooter
          sx={{
            background: theme.colors.neutral.F5F7F9,
            borderTop: `1px solid ${theme.colors.border.CFD7DF}`,
            borderRadius: '0px 0px 8px 8px',
          }}
          p="15.5px 16px"
        >
          <Flex>
            {isNoteStatusUpdating ? (
              <Flex gap="8px" alignItems="center">
                <Button onClick={onClose}>
                  <Spinner />
                  Finishing...
                </Button>
              </Flex>
            ) : (
              <Button onClick={onClose}>Finish</Button>
            )}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const ViewViolationEmployeeViewFooter = ({
  violationNoteId,
  violationStatus,
  onCancel,
  generatePdfFormData,
}: ViewViolationEmployeeViewFooterProps) => {
  const queryClient = useQueryClient();
  const [enablePolling, setEnablePolling] = useState(false);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const { data, isLoading } = useAcknowledgeNotePoll({
    noteId: violationNoteId,
    enabled: enablePolling,
  });
  const [isNoteStatusUpdating, setIsNoteStatusUpdating] = useState(false);

  const onSuccess = () => {
    const queryKeysToInvalidate = [
      [REACT_QUERY_KEYS.disciplinaryActions.getViolationNote],
      [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActions],
      [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActionsNotesStageChartData],
      [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActionsNotesCountChartData],
    ];
    queryKeysToInvalidate.forEach((key) => {
      queryClient.invalidateQueries({ queryKey: key });
    });
  };

  const isAcknowledged = violationStatus === VIOLATION_STATUS.ACKNOWLEDGED;

  const onAcknowledge = async () => {
    try {
      const formData = await generatePdfFormData();
      setShowSigningModal(true);
      const res = await uploadPdf({
        formData,
        noteId: violationNoteId,
      });
      setEnablePolling(true);
    } catch (error) {
      console.error('Error uploading PDF:', error);
    }
    // acknowledgeNote(violationNoteId);
  };

  return (
    <>
      <Flex
        justifyContent={isAcknowledged ? 'end' : 'space-between'}
        h="72px"
        w="full"
        p="16px"
        bg="surface.F5F7F9"
        boxShadow="0px -1px 2px 0px #00000014"
      >
        <Button variant={isAcknowledged ? 'filled' : 'transparentOutline'} onClick={onCancel}>
          {isAcknowledged ? 'Close' : 'Cancel'}
        </Button>
        {!isAcknowledged && (
          <Button variant="filled" onClick={onAcknowledge}>
            Sign and acknowledge
          </Button>
        )}
      </Flex>
      <SigningModal
        isOpen={showSigningModal}
        onClose={() => {
          setIsNoteStatusUpdating(true);
          setTimeout(() => {
            setShowSigningModal(false);
            onSuccess();
            setIsNoteStatusUpdating(false);
          }, 5000);
        }}
        signingLink={data?.signingLink ?? ''}
        isLoading={isLoading}
        isNoteStatusUpdating={isNoteStatusUpdating}
      />
    </>
  );
};
